<template>
  <div>
    <CallTimeline
      v-for="(task, index) in logsList"
      :key="index"
      class="mb-3"
      :tasks="task"
      :index="index"
      :contact-array="contactArray"
      :modal-form="modalForm"
      @fetchContacts="$emit('fetchContacts', $event)"
      @fetchRecords="loadMore"
    />
  </div>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api'
import { useRouter } from '@core/utils'
import { mdiChevronRight, mdiChevronDown } from '@mdi/js'
import store from '@/store'
import { storeActions } from '@/store/storeConstants'
import schema from '@/schema/taskSchema'
import moment from 'moment'
import Toaster from '@core/utils/sweetToast'
import CallTimeline from './CallTimeline.vue'

export default {
  components: {
    CallTimeline,
  },
  props: {
    propType: {
      type: String,
      required: true,
    },
    propId: {
      type: String,
      required: true,
    },
    modalForm: {
      type: Object,
      default: undefined,
    },
    contactArray: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, ctx) {
    const { route } = useRouter()
    const isVisible = ref(false)
    const activeId = ref(null)
    const panel = ref(null)
    const response = ref([])
    const count = ref(0)
    const loading = ref(false)
    const page = ref(1)
    const itemTask = ref([])
    const taskResponse = ref([])
    const taskPage = ref(1)

    const emailPage = ref(1)
    const emailCount = ref(0)
    const emailResponse = ref([])
    // view task variables
    const viewTaskModal = ref(false)
    const taskForm = ref({})
    const dateFilter = ref('all')
    const sortValue = ref('desc')

    const loadTasks = () => {
      taskPage.value = 1
      const payload = {
        component: 'task-management/spaces/item/module/list',
        object: {
          prop_id: props.propId,
          prop_type: props.propType,
          filter: {
            workflow_stage: [1],
          },
          sort: sortValue.value,
          sortby: 'updatedAt',
          date_filter: dateFilter.value === 'all' ? undefined : dateFilter.value,
        },
      }
      store
        .dispatch(`common/${storeActions.CREATE}`, payload)
        .then(({ data }) => {
          loading.value = false
          if (data.success) {
            itemTask.value = data.data.map(opt => {
              return {
                ...opt,
                updatedAt: opt.updatedAt,
                logType: 'task',
              }
            })

            taskResponse.value = itemTask.value.slice(0, 10)
          }
        })
        .catch(() => {
          loading.value = false
        })
    }

    const loadMore = () => {
      page.value = 1
      const payload = {
        component: 'task-management/logs',
        filter: {
          limit: 10,
          offset: 0,
          sortby: 'updatedAt',
          sort: sortValue.value,
          prop_id: props.propId,
          prop_type: props.propType,
          date_filter: dateFilter.value === 'all' ? undefined : dateFilter.value,
        },
      }
      store
        .dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
        .then(({ data }) => {
          loading.value = false
          if (data.success) {
            response.value = data.data.map(opt => {
              return {
                ...opt,
                logType: 'mannualLog',
                is_visible: false,
                updatedAt: opt.updatedAt,
              }
            })
            if (data.meta !== undefined) {
              count.value = data.meta.count
            }
          } else {
            response.value = []
            count.value = 0
          }
        })
        .catch(() => {
          loading.value = false
        })
    }

    const loadEmailThread = () => {
      emailPage.value = 1
      const payload = {
        component: 'email/threads',
        filter: {
          prop_id: props.propId,
          prop_type: props.propType,
          limit: 10,
          offset: 0,
          sortby: 'updatedAt',
          sort: sortValue.value,
          date_filter: dateFilter.value === 'all' ? undefined : dateFilter.value,
        },
      }
      store
        .dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
        .then(({ data }) => {
          loading.value = false
          if (data.success) {
            emailResponse.value = data.data.map(opt => {
              return {
                ...opt,
                logType: 'email',
                updatedAt: opt.updatedAt,
              }
            })
            if (data.meta !== undefined) {
              emailCount.value = data.meta.count
            }
          } else {
            emailResponse.value = []
            emailCount.value = 0
          }
        })
        .catch(() => {
          loading.value = false
        })
    }

    const logsList = computed(() => {
      // const resp = [...response.value, ...taskResponse.value, ...emailResponse.value].sort(
      //   (a, b) => {
      //     return moment(a.updatedAt).isBefore(moment(b.updatedAt)) ? 1 : -1
      //   },
      // )
      const allLogs = [...response.value, ...taskResponse.value, ...emailResponse.value]

      const sortedLogs = allLogs.sort((a, b) => {
        const aTime = moment(a.updatedAt)
        const bTime = moment(b.updatedAt)

        if (sortValue.value === 'asc') {
          return aTime.isAfter(bTime) ? 1 : -1
        }

        return aTime.isBefore(bTime) ? 1 : -1
      })

      const res = sortedLogs.reduce((newVal, log) => {
        const date = moment(log.updatedAt).format('MM YYYY')
        // eslint-disable-next-line no-param-reassign
        newVal[date] = newVal[date] || []
        newVal[date].push(log)

        return newVal
      }, {})

      return res
    })

    watch(
      () => ctx.root.$route,
      () => {
        loadMore()
        loadTasks()
        loadEmailThread()
      },
      { immediate: true },
    )

    const loadInfiniteLogs = () => {
      if (page.value * 10 < count.value) {
        page.value += 1
        const payload = {
          component: 'task-management/logs',
          filter: {
            limit: 10,
            offset: 10 * (page.value - 1),
            sortby: 'updatedAt',
            sort: 'desc',
            prop_id: route.value.params.uuid,
            prop_type: props.propType,
          },
        }
        loading.value = true
        store
          .dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
          .then(({ data }) => {
            loading.value = false
            if (data.success && data.data) {
              const responseData = data.data.map(opt => {
                return {
                  ...opt,
                  logType: 'mannualLog',
                }
              })

              response.value.push(...responseData)
              if (data.meta !== undefined) {
                count.value = data.meta.count
              }
            } else {
              response.value = []
              count.value = 0
              page.value = 1
            }
          })
          .catch(() => {
            response.value = []
            count.value = 0
            page.value = 1
          })
      }
    }
    const loadInfiniteTasks = () => {
      if (taskPage.value * 10 < itemTask.value.length) {
        taskPage.value += 1

        const nextItems = itemTask.value.slice(10 * (taskPage.value - 1), 10)

        taskResponse.value.push([...nextItems])
      }
    }
    const loadInfiniteEmail = () => {
      if (emailPage.value * 10 < emailCount.value) {
        emailPage.value += 1
        const payload = {
          component: 'email/threads',
          filter: {
            limit: 10,
            offset: 10 * (emailPage.value - 1),
            sortby: 'updatedAt',
            sort: 'desc',
            prop_id: route.value.params.uuid,
            prop_type: props.propType,
          },
        }
        store
          .dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
          .then(({ data }) => {
            loading.value = false
            if (data.success && data.data) {
              const responseData = data.data.map(opt => {
                return {
                  ...opt,
                  logType: 'email',
                }
              })

              emailResponse.value.push(...responseData)
              if (data.meta !== undefined) {
                emailCount.value = data.meta.count
              }
            } else {
              emailResponse.value = []
              emailCount.value = 0
              emailPage.value = 1
            }
          })
          .catch(() => {
            emailResponse.value = []
            emailCount.value = 0
            emailPage.value = 1
          })
      }
    }
    const infiniteScrolling = () => {
      loadInfiniteLogs()
      loadInfiniteTasks()
      loadInfiniteEmail()
    }

    const routeParams = computed(() => {
      return {
        prop_type: props.propType,
        prop_id: route.value.params.uuid,
      }
    })

    const upcomingTaskFetch = () => {
      ctx.emit('taskFetch')
      loadTasks()
    }

    return {
      loading,
      response,
      isVisible,
      loadMore,
      sortValue,
      dateFilter,
      infiniteScrolling,
      loadEmailThread,
      logsList,
      panel,
      activeId,
      viewTaskModal,
      routeParams,
      taskForm,
      loadTasks,
      upcomingTaskFetch,
      icons: {
        mdiChevronRight,
        mdiChevronDown,
      },
    }
  },
}
</script>

<style scoped></style>
